import { Asset, Folder, FolderContent } from '@/api/generatedApi';
import { useAssetDeletionDialog } from '@/components/AssetDeletionProgress/useAssetDeletionDialog';
import { resetDeletedAssetQueries } from '@/hooks/api/assets/resetDeletedAssetQueries';
import { ACTIVE_ASSET_DELETIONS_QUERY_KEY } from '@/hooks/api/assets/useActiveAssetDeletions';
import { AssetsPageObjectModel } from '@/poms/assetsPageObjectModel';
import { api } from '@/services/api';
import { useTenants } from '@/services/tenants/useTenants';
import { useToast } from '@/hooks/useToast';
import { pluralize } from '@/utils/format';
import { useQueryClient } from '@tanstack/react-query';
import { BulkDeleteModal } from './BulkDeleteModal';
import { ContentTreeItem, sortContents, itemKey } from './DeleteFolderModal/DeletionContentsTree';
import { SimpleTreeView } from '@mui/x-tree-view/SimpleTreeView';
import { Box, Typography } from '@mui/material';
import { useMemo, useState } from 'react';

const PREVIEW_LIMIT = 100;
export const MAX_ASSETS_PER_DELETION = 50_000;
const FOLDERS_DELETED_AT_ONCE = 10;

const isAsset = (content: FolderContent): content is Asset & { contentType: 'asset' } => {
  return content.contentType === 'asset';
};

export const BulkDeleteContentModal = <T extends FolderContent>({
  open,
  onClose,
  selectedContent,
  onDeleteComplete,
}: {
  open: boolean;
  onClose: () => void;
  selectedContent: T[];
  onDeleteComplete?: () => void;
}) => {
  const { getTenantId } = useTenants();
  const queryClient = useQueryClient();

  const { showToast } = useToast();
  const { beginStarting, finishStarting, trackStartedJob, close: closeProgress } = useAssetDeletionDialog();

  const startAssetJobs = async (assetIds: string[]) => {
    const jobIds: string[] = [];
    try {
      for (let start = 0; start < assetIds.length; start += MAX_ASSETS_PER_DELETION) {
        const batch = assetIds.slice(start, start + MAX_ASSETS_PER_DELETION);
        const { data } = await api.assets.startAssetDeletion(getTenantId(), { assetIds: batch }); // NOSONAR
        jobIds.push(data.id);
      }
    } catch (error) {
      if (jobIds.length === 0) throw error;
      const startedCount = (jobIds.length * MAX_ASSETS_PER_DELETION).toLocaleString();
      showToast({
        message: `Started deleting ${startedCount} of ${assetIds.length.toLocaleString()} assets. Select the rest and delete again.`,
        type: 'error',
      });
    }
    await queryClient.invalidateQueries({ queryKey: [ACTIVE_ASSET_DELETIONS_QUERY_KEY] });
    for (const id of jobIds) trackStartedJob(id);
  };

  const deleteFolders = async (folders: T[]) => {
    let failedCount = 0;
    for (let start = 0; start < folders.length; start += FOLDERS_DELETED_AT_ONCE) {
      const batch = folders.slice(start, start + FOLDERS_DELETED_AT_ONCE);
      const deletions = batch.map(folder => api.folders.deleteFolder(getTenantId(), folder.id));
      const results = await Promise.allSettled(deletions); // NOSONAR
      failedCount += results.filter(result => result.status === 'rejected').length;
    }
    resetDeletedAssetQueries(queryClient);
    if (failedCount > 0) {
      showToast({ message: `Failed to delete ${pluralize(failedCount, 'folder', 'folders')}`, type: 'error' });
    }
    return failedCount;
  };

  const startAssetsThenFolders = async (assetIds: string[], folders: T[]) => {
    beginStarting(assetIds.length);
    onClose();
    try {
      await startAssetJobs(assetIds);
      if (folders.length > 0) await deleteFolders(folders);
    } catch (error) {
      closeProgress();
      throw error;
    } finally {
      finishStarting();
    }
  };

  const startDeleting = async (contents: T[]) => {
    const assetIds = contents.filter(content => isAsset(content)).map(content => content.id);
    const folders = contents.filter(content => !isAsset(content));
    if (assetIds.length > 0) {
      await startAssetsThenFolders(assetIds, folders);
      onDeleteComplete?.();
      return;
    }
    const failedFolders = await deleteFolders(folders);
    onDeleteComplete?.();
    onClose();
    const startedCount = contents.length - failedFolders;
    return startedCount > 0 ? `Deleting ${pluralize(startedCount, 'item', 'items')} in the background` : undefined;
  };

  const previewed = useMemo(
    () => sortContents(selectedContent as FolderContent[]).slice(0, PREVIEW_LIMIT),
    [selectedContent]
  );
  const hiddenCount = selectedContent.length - previewed.length;
  const [expandedItems, setExpandedItems] = useState<string[]>([]);

  const folders = selectedContent.filter(
    (c): c is T & Folder & { contentType: 'folder' } => c.contentType === 'folder'
  );
  const selectedAssetCount = selectedContent.filter(c => isAsset(c)).length;
  const childAssetCount = folders.reduce((sum, f) => sum + f.assetsCount, 0);
  const childFolderCount = folders.reduce((sum, f) => sum + f.foldersCount, 0);
  const totalAssets = selectedAssetCount + childAssetCount;
  const totalFolders = childFolderCount;

  const buildDescription = (count: number): string => {
    const label = count === 1 ? 'item' : 'items';
    const parts: string[] = [];
    if (totalAssets > 0) {
      parts.push(`${totalAssets.toLocaleString()} ${totalAssets === 1 ? 'asset' : 'assets'}`);
    }
    if (totalFolders > 0) {
      parts.push(`${totalFolders.toLocaleString()} ${totalFolders === 1 ? 'subfolder' : 'subfolders'}`);
    }
    const including = parts.length > 0 ? `, including ${parts.join(' and ')}` : '';
    return `${count.toLocaleString()} ${label} will be permanently deleted${including}. This action cannot be undone.`;
  };

  if (!open) return;

  return (
    <BulkDeleteModal
      open={open}
      onClose={onClose}
      selectedItems={selectedContent}
      onDeleteComplete={onDeleteComplete}
      startInBackground={startDeleting}
      itemLabel={{ singular: 'item', plural: 'items' }}
      dataTestId={AssetsPageObjectModel.BulkDeleteModal}
      description={buildDescription}
    >
      <Box
        sx={{
          mt: 1,
          border: 1,
          borderColor: 'divider',
          borderRadius: 1,
          maxHeight: 250,
          overflowY: 'auto',
          p: 1,
        }}
      >
        <SimpleTreeView
          expandedItems={expandedItems}
          onExpandedItemsChange={(_event, itemIds) => setExpandedItems(itemIds)}
          sx={{
            '& .MuiTreeItem-content': {
              borderRadius: 1,
            },
          }}
        >
          {previewed.map(item => (
            <ContentTreeItem key={itemKey(item)} content={item} />
          ))}
        </SimpleTreeView>
        {hiddenCount > 0 && (
          <Typography variant='body2' color='textSecondary' sx={{ pl: 1, pt: 0.5 }}>
            and {hiddenCount.toLocaleString()} more
          </Typography>
        )}
      </Box>
    </BulkDeleteModal>
  );
};
